/**
 * NOTE on redux:
 *  - `payment.getPaymentInfo()` doesn't exist on web (the payment duck only
 *    has subscribe/checkout, per the payment-stub scope agreed earlier).
 *    Subscription status and renewal date already come from
 *    customerProfile.data (has_active_subscription, renewal_date), so this
 *    screen reads those directly instead of making an extra fetch.
 *  - `deleteAccount` is a new module built specifically for this screen — no
 *    delete-account capability existed anywhere in the redux store. Its API
 *    endpoint (/users/delete-account/) follows existing naming conventions
 *    but hasn't been confirmed against the real backend.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MedalIcon, ShareRefCodeButton, Spinner,UndrawGiftIcon, SeoHead } from 'components';
import { actions } from 'coupon-common/src/modules/customerProfile';
import { actions as userDeleteActions } from 'coupon-common/src/modules/deleteAccount';
import { dispatchAsAsync } from 'coupon-common/src/utils';

// import { useAuth } from '../../containers/AuthContext';
import { RouteNames } from '../../constants';
import messages from './messages';
import styles from './MyAccount.module.scss';

const MyAccount = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const { logout } = useAuth();

  const {
    fetching,
    fetched,
    data: {
      earned_points: earnedPoints,
      is_business_marketer: isBusinessMarketer,
      has_active_subscription: hasActiveSubscription,
      renewal_date: renewalDate,
    } = {},
    error,
  } = useSelector(state => state.customerProfile);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const renewalDateParsed = renewalDate ? new Date(renewalDate) : null;

  const getCustomerProfile = useCallback(() => {
    dispatch(actions.getCustomerProfile());
  }, [dispatch]);

  useEffect(() => {
    getCustomerProfile();
  }, [getCustomerProfile]);

  const handleDeleteUser = async () => {
    setIsDeleting(true);
    const result = await dispatchAsAsync(userDeleteActions.deleteUser(), dispatch);
    setIsDeleting(false);
    if (!result) {
      setDeleteModalVisible(false);
      // await logout();
    }
  };

  const goToPrivacyPolicy = () => navigate(RouteNames.PRIVACY_POLICY);
  const goToTermsOfService = () => navigate(RouteNames.TERMS_OF_SERVICE);
  const goToSubscribe = () => navigate(RouteNames.PAYMENT_FORM);

  if (error) {
    return (
      <div styleName="page">
        <p styleName="state-message">Something went wrong loading your account.</p>
      </div>
    );
  }

  if (!fetched) {
    return (
      <div styleName="page" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner show size="lg" />
      </div>
    );
  }

  return (
    <div styleName="page">
                    <SeoHead title="My Account" noIndex />
      <div styleName="wrap">
        <div styleName="icon-wrap">
          <UndrawGiftIcon />
        </div>

        <h1 styleName="title">
          <FormattedMessage {...messages.youEarnedPointsTitle} values={{ points: earnedPoints }} />
        </h1>
        <p styleName="larger-text">
          100 <FormattedMessage {...messages.pointText} /> = $1 CAD
        </p>
        <p styleName="base-text">
          <FormattedMessage {...messages.pointsAssignmentText} />
        </p>

        {isBusinessMarketer && (
          <div styleName="status-block">
            <MedalIcon />
            <span styleName="status-block-text">
              <FormattedMessage
                {...messages.yourStatusText}
                values={{
                  statusText: (
                    <strong styleName="text-accent">
                      <FormattedMessage {...messages.statusText} />
                    </strong>
                  ),
                }}
              />
            </span>
          </div>
        )}

        <div styleName="card">
          <h2 styleName="subtitle">
            <FormattedMessage {...messages.referralProgramCodeTitle} />
          </h2>
          <p styleName="base-text" style={{ textAlign: 'left', marginBottom: '1rem' }}>
            <FormattedMessage
              {...messages.referralCodeInstructionText}
              values={{
                targetText: (
                  <strong styleName="text-accent">
                    <FormattedMessage {...messages.referralsText} />
                  </strong>
                ),
              }}
            />
          </p>
          <ShareRefCodeButton />
        </div>

        <div styleName="card">
          <h2 styleName="subtitle">
            <FormattedMessage {...messages.paymentInformationTitle} />
          </h2>
          <p styleName="base-text" style={{ textAlign: 'left' }}>
            {!hasActiveSubscription && <FormattedMessage {...messages.hasFreePlan} />}
            {hasActiveSubscription && renewalDateParsed && (
              <FormattedMessage
                {...messages.yourRenewalDateText}
                values={{
                  renewalDateText: (
                    <strong styleName="text-accent">
                      <FormattedMessage {...messages.renewalDate} values={{ renewalDate: renewalDateParsed }} />
                    </strong>
                  ),
                }}
              />
            )}
          </p>
          {!hasActiveSubscription && (
            <button type="button" styleName="subscribe-btn" onClick={goToSubscribe}>
              <FormattedMessage {...messages.subscribe} />
            </button>
          )}
        </div>

        <div styleName="card">
          <h2 styleName="subtitle">
            <FormattedMessage {...messages.legalInformationTitle} />
          </h2>
          <div styleName="legal-list">
            <button type="button" styleName="legal-link" onClick={goToPrivacyPolicy}>
              » {intl.formatMessage(messages.privacyPolicyText)}
            </button>
            <button type="button" styleName="legal-link" onClick={goToTermsOfService}>
              » {intl.formatMessage(messages.termsOfServiceText)}
            </button>
            <button type="button" styleName="legal-link danger" onClick={() => setDeleteModalVisible(true)}>
              » {intl.formatMessage(messages.deleteAccountText)}
            </button>
          </div>
        </div>

        <p styleName="version-text">Build 179, version 3.0.1</p>
      </div>

      {deleteModalVisible && (
        <div styleName="modal-overlay" onClick={() => !isDeleting && setDeleteModalVisible(false)}>
          <div styleName="modal-content" onClick={e => e.stopPropagation()}>
            <h2 styleName="modal-title">Delete Account</h2>
            <p styleName="modal-message">Are you sure you want to delete your account? This cannot be undone.</p>
            <div styleName="modal-buttons">
              <button type="button" styleName="modal-btn-cancel" onClick={() => setDeleteModalVisible(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button type="button" styleName="modal-btn-confirm" onClick={handleDeleteUser} disabled={isDeleting}>
                {isDeleting ? <Spinner show size="sm" /> : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSSModule(MyAccount, styles, { allowMultiple: true });
